import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Chip,
  Pagination,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import axios from 'axios';
import { debounce } from 'lodash';
import { getConfig } from '../../config.js';
// Theme Colors
const themeColors = {
  primary: '#4CAF50', // Updated green color
  secondary: '#FF9800', // Orange for highlights
  background: '#F4F7F9', // Light background
  white: '#FFFFFF',
  grey: '#686978',
  discount: '#FF5722', // Red-Orange for discounts
};

// API Config
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
const AUTH_TOKEN = '045e6c0d-8ed7-480e-820e-6b8f4261cb51';

const PharmacyProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const fetchProducts = debounce(async (query, page) => {
    setLoading(true);
    const offset = (page - 1) * limit;

    try {
      const response = await axios.get(
        `${BASE_URL}/pharmacy/products?query=${query}&offset=${offset}&limit=${limit}&latitude=26.8505899&longitude=75.7909157`,
        {
          headers: { Authorization: AUTH_TOKEN },
        }
      );

      const data = response.data;
      if (data.status) {
        setProducts(data.data);
        setTotalPages(Math.ceil(data.data.length / limit));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchProducts('', 1);
  }, []);

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
    fetchProducts(query, 1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchProducts(searchQuery, value);
  };

  return (
    <Box sx={{ backgroundColor: themeColors.background, minHeight: '100vh', padding: '20px' }}>
      {/* Elegant Search Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          backgroundColor: themeColors.white,
          borderRadius: '24px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          padding: '8px 16px',
        }}
      >
        <TextField
          fullWidth
          placeholder="Search for medicines..."
          value={searchQuery}
          onChange={handleSearch}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
              paddingRight: '8px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Product List */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '20px',
            }}
          >
            {products.map((product) => (
              <Card
                key={product.reference_id}
                sx={{
                  borderRadius: '16px',
                  boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              >
                <CardContent>
                  {/* Image Placeholder */}
                  <Box
                    sx={{
                      height: '150px',
                      background: `linear-gradient(135deg, ${themeColors.primary} 30%, ${themeColors.secondary} 90%)`,
                      borderRadius: '8px',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: themeColors.white,
                      fontWeight: 'bold',
                    }}
                  >
                    {product.image.code || 'No Image'}
                  </Box>

                  {/* Product Details */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {product.name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '8px' }}>
                    {product.desc}
                  </Typography>

                  <Chip
                    label={product.prescription_required ? 'Prescription Required' : 'OTC'}
                    color={product.prescription_required ? 'error' : 'success'}
                    size="small"
                    sx={{ marginBottom: '8px' }}
                  />

                  <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    ₹{product.price.toFixed(2)}{' '}
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ textDecoration: 'line-through', marginLeft: '8px', color: 'text.secondary' }}
                    >
                      ₹{product.mrp.toFixed(2)}
                    </Typography>
                  </Typography>
                  <Chip label={product.discount_tag} sx={{ backgroundColor: themeColors.discount, color: themeColors.white }} />

                  {/* Add to Cart */}
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ marginTop: '12px', backgroundColor: themeColors.primary }}
                    startIcon={<AddShoppingCartIcon />}
                  >
                    Add to Cart
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
          </Box>
        </>
      )}
    </Box>
  );
};

export default PharmacyProducts;